import { useState, Fragment } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Dialog, Menu, Transition } from '@headlessui/react'
import { 
  Bars3Icon, 
  XMarkIcon, 
  ChevronDownIcon, 
  HeartIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'
import DonateModal from './DonateModal'

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [donateModalOpen, setDonateModalOpen] = useState(false)
  const location = useLocation()

  const isAboutActive = ['/about', '/team', '/goodwill-medical'].includes(location.pathname)

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'Programs', href: '/programs' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'Media & News', href: '/media' },
    { name: 'Reports', href: '/reports' },
    { name: 'Contact', href: '/contact' },
  ]

  const aboutDropdown = [
    { 
      name: 'About PACIEH', 
      href: '/about', 
      desc: 'Our mission, history, and core values',
      icon: InformationCircleIcon 
    },
    { 
      name: 'Leadership & Team', 
      href: '/team', 
      desc: 'Board of Trustees and technical advisors',
      icon: UserGroupIcon 
    },
    { 
      name: 'Goodwill Medical Centre', 
      href: '/goodwill-medical', 
      desc: 'Flagship community hospital in Umuchigbo',
      icon: BuildingOffice2Icon 
    },
  ]

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-xs transition-all">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center justify-between h-20" aria-label="Global">
            {/* Logo with Bold Typography */}
            <div className="flex lg:flex-1 items-center">
              <Link to="/" className="flex items-center gap-3 group">
                <div className="w-10 h-10 rounded-2xl bg-forest flex items-center justify-center text-[#8de058] font-black font-display text-xl shadow-md group-hover:scale-105 transition-transform">
                  P
                </div>
                <div className="flex flex-col">
                  <span className="text-2xl font-black text-forest tracking-tighter font-display leading-none">
                    PACIEH
                  </span>
                  <span className="text-[10px] text-gray-500 font-extrabold tracking-wider uppercase mt-0.5">
                    Education & Healthcare
                  </span>
                </div>
              </Link>
            </div>

            {/* Mobile Actions */}
            <div className="flex lg:hidden items-center gap-3">
              <button
                type="button"
                onClick={() => setDonateModalOpen(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#8de058] text-[#072617] text-xs font-black shadow-sm"
              >
                Donate →
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-2xl p-2.5 text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={() => setMobileMenuOpen(true)}
              >
                <span className="sr-only">Open main menu</span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex lg:items-center lg:gap-x-7">
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `text-sm font-bold tracking-tight transition-colors ${
                    isActive ? 'text-forest' : 'text-gray-700 hover:text-forest'
                  }`
                }
              >
                Home
              </NavLink>

              {/* About Dropdown Menu */}
              <Menu as="div" className="relative">
                <Menu.Button
                  className={`inline-flex items-center gap-1 text-sm font-bold tracking-tight transition-colors focus:outline-none ${
                    isAboutActive ? 'text-forest' : 'text-gray-700 hover:text-forest'
                  }`}
                >
                  About Us
                  <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
                </Menu.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <Menu.Items className="absolute left-0 mt-3 w-72 origin-top-left rounded-2xl bg-white p-2 shadow-2xl ring-1 ring-black/5 focus:outline-none border border-gray-100 divide-y divide-gray-50">
                    {aboutDropdown.map((item) => {
                      const Icon = item.icon
                      return (
                        <Menu.Item key={item.name}>
                          {({ active }) => (
                            <Link
                              to={item.href}
                              className={`flex items-start gap-3 p-3 rounded-xl transition-all ${
                                active ? 'bg-[#F4F7F4]' : ''
                              }`}
                            >
                              <div className="p-2 rounded-xl bg-forest/10 text-forest shrink-0">
                                <Icon className="h-5 w-5" />
                              </div>
                              <div>
                                <span className="block text-sm font-bold text-gray-900 leading-tight">
                                  {item.name}
                                </span>
                                <span className="block text-xs text-gray-500 mt-0.5 leading-snug">
                                  {item.desc}
                                </span>
                              </div>
                            </Link>
                          )}
                        </Menu.Item>
                      )
                    })}
                  </Menu.Items>
                </Transition>
              </Menu>

              {navLinks.slice(1).map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    `text-sm font-bold tracking-tight transition-colors ${
                      isActive ? 'text-forest' : 'text-gray-700 hover:text-forest'
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              ))}
            </div>

            {/* Right Action: Pill Button with circle arrow (Inspired by Brightaid) */}
            <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:items-center lg:gap-4">
              <button
                type="button"
                onClick={() => setDonateModalOpen(true)}
                className="btn-pill-lime group py-2.5 px-5"
              >
                Donate Now
                <span className="w-5 h-5 rounded-full bg-[#072617] text-white flex items-center justify-center text-xs group-hover:translate-x-0.5 transition-transform">
                  →
                </span>
              </button>
            </div>
          </nav>
        </div>

        {/* Mobile Menu Dialog */}
        <Dialog as="div" className="lg:hidden" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
          <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
          <Dialog.Panel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm shadow-2xl">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100">
              <Link to="/" className="flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <div className="w-9 h-9 rounded-2xl bg-forest flex items-center justify-center text-[#8de058] font-black font-display text-lg">
                  P
                </div>
                <span className="text-xl font-black text-forest font-display">PACIEH</span>
              </Link>
              <button
                type="button"
                className="rounded-xl p-2 text-gray-500 hover:bg-gray-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            <div className="mt-6 flow-root">
              <div className="space-y-1">
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    `block rounded-xl px-4 py-2.5 text-base font-bold ${
                      isActive ? 'bg-[#EDF4ED] text-forest' : 'text-gray-900 hover:bg-gray-50'
                    }`
                  }
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Home
                </NavLink>

                {/* About Section in Mobile */}
                <div className="py-2">
                  <span className="px-4 text-xs font-black uppercase tracking-wider text-gray-400 block mb-1">
                    About PACIEH
                  </span>
                  {aboutDropdown.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) =>
                        `block rounded-xl px-4 py-2 text-sm font-semibold ${
                          isActive ? 'bg-[#EDF4ED] text-forest font-bold' : 'text-gray-700 hover:bg-gray-50'
                        }`
                      }
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </NavLink>
                  ))}
                </div>

                {navLinks.slice(1).map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      `block rounded-xl px-4 py-2.5 text-base font-bold ${
                        isActive ? 'bg-[#EDF4ED] text-forest' : 'text-gray-900 hover:bg-gray-50'
                      }`
                    }
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </NavLink>
                ))}
              </div>

              {/* Action Buttons in Mobile */}
              <div className="mt-8 pt-6 border-t border-gray-100 space-y-3">
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false)
                    setDonateModalOpen(true)
                  }}
                  className="w-full btn-pill-lime py-3.5 group justify-center"
                >
                  Donate to PACIEH
                  <span className="w-5 h-5 rounded-full bg-[#072617] text-white flex items-center justify-center text-xs">
                    →
                  </span>
                </button>
                <Link
                  to="/contact"
                  className="w-full inline-flex items-center justify-center py-3 rounded-full border border-gray-200 text-gray-800 font-bold hover:bg-gray-50 transition-colors text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Get Involved / Volunteer
                </Link>
              </div>
            </div>
          </Dialog.Panel>
        </Dialog>
      </header>

      {/* Global Donation Modal */}
      <DonateModal
        isOpen={donateModalOpen}
        onClose={() => setDonateModalOpen(false)}
      />
    </>
  )
}